/**
 * The small pieces every screen that shows a visit shares, so a cancelled visit reads the same way
 * on the Today screen, on a cleaner's list and on the visit itself.
 *
 * Coral is used sparingly in this interface. Here it marks the two things on a schedule that need
 * somebody to act: a visit nobody is assigned to, and a change the client has not been told about.
 *
 * A VISIT'S STATUS IS NOT PAINTED HERE. It is painted by `VisitStatusPill` in `StatusPills.tsx`
 * and nowhere else. This file used to hold a second one, kept a copy of the tone rule, and that
 * copy said cancelled and no access were coral, so one screen drew a settled fact as an alarm.
 */
import type { CrewOnVisit } from '../api.ts';
import { dayOf, longDay } from '../format.ts';
import { Pill } from './AppShell.tsx';

/** The one pill that means somebody has to do something before the day arrives. */
export function NotToldPill() {
  return <Pill tone="coral">Not told yet</Pill>;
}

/**
 * WHO IS GOING (ops #74 item 5): the crew less anybody who declined in the app. A cleaner who declined
 * is not coming, so no screen names them as on the visit; the office's own view says who declined and
 * why, beside the crew rather than in it.
 */
export function onTheCrew<T extends Pick<CrewOnVisit, 'status'>>(cleaners: readonly T[]): T[] {
  return cleaners.filter((cleaner) => cleaner.status !== 'declined');
}

/**
 * A VISIT THAT NEEDS A CLEANER: somebody was on it and every one of them declined. Drawn in coral as an
 * open call-off is, since the office has to put somebody on it before the day (decision 3: the schedule
 * is how the office learns of a decline). A visit nobody was ever put on is "Nobody assigned" instead.
 */
export function needsACleaner(cleaners: readonly Pick<CrewOnVisit, 'status'>[]): boolean {
  return cleaners.length > 0 && onTheCrew(cleaners).length === 0;
}

/** "Declined by Maria on Tuesday, September 8: Away that week", or without the reason where none was given. */
export function declinedWords(
  cleaner: Pick<CrewOnVisit, 'displayName' | 'respondedAt' | 'declineReason'>,
): string {
  const day = dayOf(cleaner.respondedAt ?? null);
  const said = `Declined by ${cleaner.displayName}${day ? ` on ${longDay(day)}` : ''}`;
  return cleaner.declineReason ? `${said}: ${cleaner.declineReason}` : `${said}.`;
}

/**
 * Names, joined the way they are said, of the cleaners going: anybody who declined is left out. Empty
 * is called out, because an unassigned visit is a gap.
 */
export function crewNames(cleaners: readonly CrewOnVisit[]): string | null {
  const names = onTheCrew(cleaners).map((cleaner) => cleaner.displayName);
  if (names.length === 0) return null;
  if (names.length === 1) return names[0] ?? null;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export function NobodyAssigned() {
  return <p className="text-sm text-coral">Nobody assigned.</p>;
}

/** A visible checkbox with a real label, 44px tall, for the two confirmations the server stamps. */
export function CheckField({
  id,
  label,
  checked,
  onChange,
  hint,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
}) {
  return (
    <div className="mb-4">
      <label
        htmlFor={id}
        className="flex min-h-11 desk:min-h-9 items-center gap-3 text-sm font-medium"
      >
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          className="h-5 w-5 shrink-0 accent-navy"
        />
        <span>{label}</span>
      </label>
      {hint ? <p className="mt-1 pl-8 text-xs text-navy/65">{hint}</p> : null}
    </div>
  );
}
